import type { BoardStage } from 'chery-contracts';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { AccountabilityMatrix } from './AccountabilityMatrix';
import { stageFromSlug } from './board-display';
import { BoardHeader } from './BoardHeader';
import { DIVISION_CARDS, OPERATING_MODEL_INTRO, STAGES } from './content/divisions';
import { DivisionCardView } from './DivisionCardView';
import { OrgChart } from './OrgChart';
import { StageSheets } from './StageSheets';

/**
 * Page 02 — the operating model (plan §7): the org chart, the accountability matrix and the division cards are static
 * copy; the stage tabs open each stage's divisions and its live, printable report sheets (`?stage=selling`).
 */
export const OperatingModelPage = () => {
  const { t } = useI18nTranslation();
  const [params, setParams] = useSearchParams();
  const stage = stageFromSlug(params.get('stage'));

  const select = (next: BoardStage): void => {
    const slug = STAGES.find((each) => each.stage === next)?.slug ?? 'getting';

    setParams({ stage: slug }, { replace: true });
  };

  return (
    <div
      className="space-y-6"
      data-board-page="operating-model"
    >
      <div className="no-print space-y-6">
        <BoardHeader {...OPERATING_MODEL_INTRO} />
        <OrgChart />
        <AccountabilityMatrix
          selected={stage.stage}
          onSelect={select}
        />
      </div>

      <div className="no-print space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('board.sheets.stages')}</p>
        <div
          className="flex flex-wrap gap-2"
          role="tablist"
        >
          {STAGES.map((each) => (
            <button
              key={each.stage}
              type="button"
              role="tab"
              aria-selected={each.stage === stage.stage}
              data-stage-tab={each.slug}
              onClick={() => select(each.stage)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm transition-colors',
                each.stage === stage.stage
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:bg-muted',
              )}
            >
              {each.name}
            </button>
          ))}
        </div>
      </div>

      <div className="no-print grid gap-4 xl:grid-cols-2">
        {stage.divisionIds.map((id) => {
          const card = DIVISION_CARDS[id];

          return card ? (
            <DivisionCardView
              key={id}
              card={card}
            />
          ) : null;
        })}
      </div>

      <StageSheets stage={stage} />
    </div>
  );
};
